(() => {
  if (!/SamsungBrowser\//i.test(navigator.userAgent)) return;
  const selector = '#MainContent img, .vovi-preview .vovi-paper svg, .vovi-dialog-art > svg, .vovi-dialog-art > img';
  const records = new Map();
  const resources = new Map();
  const style = document.createElement('style');
  style.textContent = '[data-vovi-color-protected]{visibility:hidden!important}canvas[data-vovi-color-canvas]{pointer-events:none!important}';
  document.head.append(style);
  const inline = url => {
    if (url.startsWith('data:')) return Promise.resolve(url);
    const absolute = new URL(url, location.href).href;
    if (!resources.has(absolute)) {
      resources.set(absolute, fetch(absolute).then(response => {
        if (!response.ok) throw Error('Image unavailable');
        return response.blob();
      }).then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })).catch(error => { resources.delete(absolute); throw error; }));
    }
    return resources.get(absolute);
  };
  const eligible = source => !source.closest('svg svg') &&
    !(source.tagName.toLowerCase() === 'svg' && source.parentElement.closest('svg')) &&
    !source.closest('.vovi-map-loading');
  function restore(record) {
    if (record.source.hasAttribute('data-vovi-color-protected')) record.source.removeAttribute('data-vovi-color-protected');
    if (!record.canvas.hidden) record.canvas.hidden = true;
  }
  function place(record) {
    const { source, canvas } = record;
    if (!source.isConnected || !source.parentElement) return;
    const css = getComputedStyle(source);
    if (!source.getClientRects().length || !source.clientWidth || !source.clientHeight) {
      if (!canvas.hidden) canvas.hidden = true;
      return;
    }
    const parent = source.parentElement;
    if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
    const bounds = source.getBoundingClientRect(), parentBounds = parent.getBoundingClientRect();
    const parentScaleX = parentBounds.width / parent.offsetWidth || 1;
    const parentScaleY = parentBounds.height / parent.offsetHeight || 1;
    const left = source.offsetLeft ?? ((bounds.left - parentBounds.left) / parentScaleX - parent.clientLeft + parent.scrollLeft);
    const top = source.offsetTop ?? ((bounds.top - parentBounds.top) / parentScaleY - parent.clientTop + parent.scrollTop);
    const isOverlay = source.classList.contains('vovi-motion-overlay');
    const properties = {
      position: 'absolute', left: `${left}px`, top: `${top}px`,
      width: `${source.clientWidth}px`, height: `${source.clientHeight}px`,
      margin: '0px', padding: '0px', border: '0px', maxWidth: 'none', maxHeight: 'none',
      objectFit: css.objectFit, objectPosition: css.objectPosition,
      borderRadius: css.borderRadius, transform: css.transform,
      transformOrigin: css.transformOrigin, filter: css.filter,
      opacity: isOverlay ? (source.classList.contains('is-visible') ? '1' : '0') : css.opacity,
      transition: isOverlay ? 'opacity 800ms ease' : 'none',
      zIndex: css.zIndex === 'auto' ? '0' : css.zIndex
    };
    for (const [property, value] of Object.entries(properties)) {
      if (canvas.style[property] !== value) canvas.style[property] = value;
    }
    const label = source.getAttribute('alt') || source.getAttribute('aria-label') || 'Produktvorschau';
    if (canvas.getAttribute('aria-label') !== label) canvas.setAttribute('aria-label', label);
    const decorative = source.getAttribute('aria-hidden') === 'true' || source.getAttribute('alt') === '';
    if (decorative && canvas.getAttribute('aria-hidden') !== 'true') canvas.setAttribute('aria-hidden', 'true');
    else if (!decorative && canvas.hasAttribute('aria-hidden')) canvas.removeAttribute('aria-hidden');
    if (canvas.hidden !== !record.ready) canvas.hidden = !record.ready;
  }
  async function paint(record) {
    const { source, canvas } = record;
    const revision = ++record.revision;
    let objectURL;
    try {
      if (!source.isConnected) return;
      const density = Math.min(3, window.devicePixelRatio || 1) * (source.closest('.vovi-dialog-art') ? 4 : 1.5);
      const size = `${source.clientWidth}:${source.clientHeight}:${density}`;
      let image, key;
      if (source.tagName.toLowerCase() === 'img') {
        key = source.currentSrc || source.src;
        if (!source.complete || !source.naturalWidth || !key) { restore(record); return; }
        if (key === record.key && size === record.size && record.ready) { place(record); return; }
        image = source;
      } else {
        const clone = source.cloneNode(true);
        clone.removeAttribute('data-vovi-color-protected');
        key = new XMLSerializer().serializeToString(clone);
        if (key === record.key && size === record.size && record.ready) { place(record); return; }
        restore(record);
        const vb = source.viewBox.baseVal;
        const width = vb.width || source.clientWidth, height = vb.height || source.clientHeight;
        if (!width || !height) return;
        const scale = Math.min(2000 / Math.max(width, height),
          Math.max(source.clientWidth / width, source.clientHeight / height) * density);
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        clone.setAttribute('width', String(Math.round(width * scale)));
        clone.setAttribute('height', String(Math.round(height * scale)));
        clone.removeAttribute('style');
        await Promise.all(Array.from(clone.querySelectorAll('image')).map(async element => {
          const url = element.getAttribute('href') || element.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
          if (url && !url.startsWith('#')) {
            element.setAttribute('href', await inline(url));
            element.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
          }
        }));
        objectURL = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' }));
        image = new Image();
        image.src = objectURL;
        await image.decode();
      }
      if (revision !== record.revision || !source.isConnected) return;
      const scale = Math.min(1, 2000 / Math.max(image.naturalWidth, image.naturalHeight),
        Math.max(source.clientWidth / image.naturalWidth, source.clientHeight / image.naturalHeight) * density);
      const buffer = document.createElement('canvas');
      buffer.width = Math.max(1, Math.round(image.naturalWidth * scale));
      buffer.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = buffer.getContext('2d');
      if (!context) throw Error('Canvas unavailable');
      context.drawImage(image, 0, 0, buffer.width, buffer.height);
      canvas.width = buffer.width;
      canvas.height = buffer.height;
      const target = canvas.getContext('2d');
      if (!target) throw Error('Canvas unavailable');
      target.drawImage(buffer, 0, 0);
      record.key = key;
      record.size = size;
      record.ready = true;
      source.setAttribute('data-vovi-color-protected', '');
      place(record);
    } catch {
      if (revision === record.revision) { record.ready = false; restore(record); }
    } finally {
      if (objectURL) URL.revokeObjectURL(objectURL);
    }
  }
  const sizes = new ResizeObserver(entries => {
    for (const { target } of entries) {
      const record = records.get(target);
      if (record) { place(record); if (record.visible) paint(record); }
    }
  });
  const visible = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const record = records.get(entry.target);
      if (!record) continue;
      record.visible = entry.isIntersecting;
      if (record.visible) paint(record);
    }
  }, { rootMargin: '200px' });
  function scan() {
    for (const [source, record] of records) {
      if (!source.isConnected) {
        record.revision++;
        record.canvas.remove();
        sizes.unobserve(source);
        visible.unobserve(source);
        records.delete(source);
      }
    }
    document.querySelectorAll(selector).forEach(source => {
      if (!eligible(source)) return;
      let record = records.get(source);
      if (!record) {
        source.removeAttribute('data-vovi-color-protected');
        const canvas = document.createElement('canvas');
        canvas.setAttribute('data-vovi-color-canvas', '');
        canvas.setAttribute('role', 'img');
        canvas.hidden = true;
        record = { source, canvas, revision: 0, ready: false, visible: false };
        records.set(source, record);
        source.after(canvas);
        sizes.observe(source);
        visible.observe(source);
      }
      place(record);
      if (record.visible) paint(record);
    });
  }
  let scheduled;
  const schedule = () => { clearTimeout(scheduled); scheduled = setTimeout(scan, 80); };
  new MutationObserver(changes => {
    if (changes.some(change => {
      if (change.target.nodeType === 1 && change.target.matches('canvas[data-vovi-color-canvas]')) return false;
      if (change.type === 'attributes' && change.attributeName === 'data-vovi-color-protected') return false;
      if (change.type === 'childList' && [...change.addedNodes, ...change.removedNodes].every(node => node.nodeType === 1 && node.matches('canvas[data-vovi-color-canvas]'))) return false;
      return true;
    })) schedule();
  }).observe(document.body, { subtree: true, childList: true, attributes: true, characterData: true });
  document.addEventListener('load', event => {
    const record = records.get(event.target);
    if (record) { record.key = null; if (record.visible) paint(record); }
  }, true);
  document.addEventListener('error', event => {
    const record = records.get(event.target);
    if (record) { record.revision++; record.ready = false; restore(record); }
  }, true);
  window.addEventListener('resize', schedule);
  window.addEventListener('pageshow', schedule);
  scan();
})();
